import React, { useCallback, useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import {
  Lock, LogOut, RefreshCw, Loader2, Phone, Mail, Truck, Store,
  PackageCheck, ChefHat, CheckCircle2, XCircle, Inbox,
} from 'lucide-react';
import api from '../../services/api';

const SECRET_KEY = 'ndms-admin-secret';

type OrderStatus = 'received' | 'preparing' | 'ready' | 'completed' | 'cancelled';

interface AdminOrder {
  orderId: string;
  status: OrderStatus;
  createdAt?: string;
  updatedAt?: string;
  customer?: { name?: string; email?: string; phone?: string; notes?: string };
  fulfillment?: { method?: string; address?: string; contactPreference?: string; timeWindow?: string };
  items?: Array<{ brand?: string; productName?: string; variantDescription?: string; quantity?: number }>;
}

const STATUS_STYLES: Record<OrderStatus, string> = {
  received: 'bg-blue-100 text-blue-700',
  preparing: 'bg-amber-100 text-amber-700',
  ready: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-500',
  cancelled: 'bg-red-100 text-red-600',
};

// Sensible next actions per current status
const NEXT_ACTIONS: Record<OrderStatus, Array<{ to: OrderStatus; label: string; icon: React.ComponentType<any> }>> = {
  received: [
    { to: 'preparing', label: 'Start preparing', icon: ChefHat },
    { to: 'ready', label: 'Mark ready', icon: PackageCheck },
    { to: 'cancelled', label: 'Cancel', icon: XCircle },
  ],
  preparing: [
    { to: 'ready', label: 'Mark ready', icon: PackageCheck },
    { to: 'cancelled', label: 'Cancel', icon: XCircle },
  ],
  ready: [
    { to: 'completed', label: 'Picked up / delivered', icon: CheckCircle2 },
    { to: 'cancelled', label: 'Cancel', icon: XCircle },
  ],
  completed: [],
  cancelled: [{ to: 'received', label: 'Reopen', icon: Inbox }],
};

export function AdminOrdersPage() {
  const [secret, setSecret] = useState<string>(() => sessionStorage.getItem(SECRET_KEY) || '');
  const [input, setInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);

  const load = useCallback(async (s: string) => {
    setLoading(true);
    try {
      const { data } = await api.get<AdminOrder[]>('/orders', {
        headers: { 'X-Admin-Secret': s },
        params: { limit: 100 },
      });
      setOrders(data);
      setAuthed(true);
      sessionStorage.setItem(SECRET_KEY, s);
      setSecret(s);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error('Wrong admin secret');
        sessionStorage.removeItem(SECRET_KEY);
        setAuthed(false);
      } else if (status === 429) {
        toast.error('Locked out — too many failed attempts. Wait 15 minutes.');
      } else {
        toast.error('Could not load orders — is the API up?');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-login if a secret is stored from this browser session
  useEffect(() => {
    if (secret) load(secret);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = useCallback(async (orderId: string, to: OrderStatus) => {
    setUpdating(orderId);
    try {
      const { data } = await api.patch(
        `/orders/${encodeURIComponent(orderId)}/status`,
        { status: to },
        { headers: { 'X-Admin-Secret': secret } }
      );
      setOrders((prev) => prev.map((o) => (o.orderId === orderId ? { ...o, ...data.order } : o)));
      if (data.customerNotified === true) toast.success(`${orderId} → ${to} · customer emailed`);
      else if (data.customerNotified === false) toast.error(`${orderId} → ${to} · EMAIL FAILED — call the customer`, { duration: 6000 });
      else toast.success(`${orderId} → ${to}`);
    } catch {
      toast.error('Update failed');
    } finally {
      setUpdating(null);
    }
  }, [secret]);

  const logout = () => {
    sessionStorage.removeItem(SECRET_KEY);
    setSecret('');
    setInput('');
    setAuthed(false);
    setOrders([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <form
          onSubmit={(e) => { e.preventDefault(); if (input.trim()) load(input.trim()); }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 w-full max-w-sm"
        >
          <div className="w-12 h-12 bg-ndms-navy rounded-2xl flex items-center justify-center mb-4">
            <Lock size={20} className="text-white" />
          </div>
          <h1 className="font-bold text-ndms-navy text-lg mb-1">Staff Orders</h1>
          <p className="text-xs text-gray-400 mb-5">Enter the admin secret to manage orders.</p>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin secret"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-ndms-navy focus:ring-1 focus:ring-ndms-navy/20 mb-3"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 bg-ndms-navy text-white font-bold rounded-xl hover:bg-ndms-navy-light transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Unlock'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />
      {/* Header */}
      <div className="bg-ndms-navy text-white px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h1 className="font-bold text-lg leading-tight">Orders</h1>
          <p className="text-xs text-white/60">{orders.length} loaded · newest first</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(secret)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
          >
            <LogOut size={14} /> Lock
          </button>
        </div>
      </div>

      {/* Orders */}
      <div className="p-4 sm:p-8 max-w-4xl mx-auto space-y-4">
        {orders.length === 0 && !loading && (
          <div className="text-center py-16 text-gray-400">
            <Inbox size={36} className="mx-auto mb-3 opacity-40" />
            <p className="font-medium">No orders yet</p>
          </div>
        )}
        {orders.map((o) => {
          const f = o.fulfillment || {};
          const c = o.customer || {};
          const isDelivery = f.method === 'delivery';
          return (
            <div key={o.orderId} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-ndms-navy text-sm">{o.orderId}</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${STATUS_STYLES[o.status] || STATUS_STYLES.received}`}>
                      {o.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {o.createdAt ? new Date(o.createdAt).toLocaleString() : ''}
                  </p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {(NEXT_ACTIONS[o.status] || []).map(({ to, label, icon: Icon }) => (
                    <button
                      key={to}
                      onClick={() => setStatus(o.orderId, to)}
                      disabled={updating === o.orderId}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 ${
                        to === 'cancelled'
                          ? 'border-red-200 text-red-600 hover:bg-red-50'
                          : 'border-ndms-navy/20 text-ndms-navy hover:bg-ndms-navy hover:text-white'
                      }`}
                    >
                      {updating === o.orderId ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer + fulfillment */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-xs space-y-1">
                  <p className="font-bold text-gray-700">{c.name || '—'}</p>
                  {c.phone && (
                    <p className="flex items-center gap-1.5 text-gray-500">
                      <Phone size={11} /> <a className="hover:underline" href={`tel:${c.phone}`}>{c.phone}</a>
                    </p>
                  )}
                  {c.email && (
                    <p className="flex items-center gap-1.5 text-gray-500">
                      <Mail size={11} /> <a className="hover:underline" href={`mailto:${c.email}`}>{c.email}</a>
                    </p>
                  )}
                  {c.notes && <p className="text-gray-500 italic">“{c.notes}”</p>}
                </div>
                <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-xs space-y-1">
                  <p className="flex items-center gap-1.5 font-bold text-gray-700">
                    {isDelivery ? <Truck size={12} /> : <Store size={12} />}
                    {isDelivery ? 'Delivery' : 'Pickup at store'}
                  </p>
                  {isDelivery && f.address && <p className="text-gray-500">{f.address}</p>}
                  <p className="text-gray-500">Contact via: {f.contactPreference || 'call'}</p>
                  <p className="text-gray-500">Preferred time: {f.timeWindow || 'any'}</p>
                </div>
              </div>

              {/* Items */}
              <ul className="space-y-1">
                {(o.items || []).map((item, i) => (
                  <li key={i} className="flex justify-between gap-3 text-xs text-gray-600 border-b border-gray-50 pb-1 last:border-0">
                    <span className="min-w-0">{item.brand} – {item.productName}{item.variantDescription ? ` · ${item.variantDescription}` : ''}</span>
                    <span className="font-bold flex-shrink-0">×{item.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
